import { Button } from '@monorepo/shared/componentsV2/Button';
import { SelectField } from '@monorepo/shared/componentsV2/fields/SelectField';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';

const TOP_LEVEL_VALUE = '__top_level__';

interface FormValues {
  folderId: string;
}

export interface MoveDocumentModalProps {
  folders: Array<{ id: string; name: string }>;
  allowTopLevel: boolean;
  onMove: (folderId: string | null) => Promise<void>;
  onClose: () => void;
  destinationLabel?: string;
  destinationPlaceholder?: string;
}

// Drag-and-drop to move files is not keyboard accessible, so we also provide a modal for moving filers.
export function MoveDocumentModal({
  folders,
  allowTopLevel,
  onMove,
  onClose,
  destinationLabel = 'Destination',
  destinationPlaceholder = 'Select a destination',
}: MoveDocumentModalProps) {
  const { showUserFriendlyErrorToast, success } = useToast();

  const options = useMemo(() => {
    const folderOptions = folders.map((folder) => ({
      value: folder.id,
      label: folder.name,
    }));
    return allowTopLevel
      ? [
          { value: TOP_LEVEL_VALUE, label: 'No folder (top level)' },
          ...folderOptions,
        ]
      : folderOptions;
  }, [allowTopLevel, folders]);

  const initialValue = useMemo(() => {
    const onlyOption = options.length === 1 ? options[0] : undefined;
    return onlyOption ? { folderId: onlyOption.value } : undefined;
  }, [options]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const folderId =
        values.folderId === TOP_LEVEL_VALUE ? null : values.folderId;
      try {
        await onMove(folderId);
        success('Document was moved.');
        onClose();
      } catch (err) {
        showUserFriendlyErrorToast(err, 'Unable to move document.');
      }
    },
    [onClose, onMove, showUserFriendlyErrorToast, success],
  );

  return (
    <Form<FormValues>
      onSubmit={onSubmit}
      initialValues={initialValue}
      subscription={{ submitting: true, values: true }}
    >
      {({ handleSubmit, submitting, values }) => (
        <BaseModal
          open
          onClose={submitting ? undefined : onClose}
          showCloseButton
          title="Move"
          maxWidth="sm"
          buttons={[
            <Button
              key="cancel"
              variant="text"
              color="primary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>,
            <Button
              key="save"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={submitting || !values.folderId}
            >
              {submitting ? 'Moving...' : 'Move'}
            </Button>,
          ]}
        >
          <SelectField
            name="folderId"
            label={destinationLabel}
            placeholder={destinationPlaceholder}
            options={options}
            noOptionsText="No destination available"
            fullWidth
          />
        </BaseModal>
      )}
    </Form>
  );
}
